import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import Spinner from '../../../shared/spinner';
import CustomAlert from '../../../shared/customAlert';
import ConsultaService from '../../../services/consultaService';
import EmpregadorService from '../../../services/empregadorService';
import PontoVendaService from '../../../services/pontoVendaService';
import PromotoraService from '../../../services/promotoraService';
import ResponsivePagination from 'react-responsive-pagination';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faRefresh } from '@fortawesome/free-solid-svg-icons';
import ScrollToTopButton from '../../../shared/scrollToTopButton';

interface Consulta {
  id: number;
  origem: string;
  idOrigem: number;
  idEmpregador: number;
  data: string;
  nconsultas: string;
  nomeEmpregador: string;
  nomePromotora: string;
}

interface ConsultaPage {
  content: Consulta[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

interface OrigemOption {
  id: number;
  nome: string;
}

type OrigemType = 'PONTO_VENDA' | 'PROMOTORA' | '';

const OPCOES_ORIGEM = [
  { value: 'PONTO_VENDA' as OrigemType, label: 'Ponto de Venda' },
  { value: 'PROMOTORA' as OrigemType, label: 'Promotora' },
];

function ListaConsultas() {
  const [loading, setLoading] = useState(false);
  const [consultas, setConsultas] = useState<Consulta[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'warning' | 'error' } | null>(null);

  const [filtros, setFiltros] = useState(false);
  const [origem, setOrigem] = useState<OrigemType>(''); 
  const [idOrigem, setIdOrigem] = useState('');
  const [idEmpregador, setIdEmpregador] = useState('');
  const [dataInicial, setDataInicial] = useState('');
  const [dataFinal, setDataFinal] = useState('');
  
  const [opcoesOrigem, setOpcoesOrigem] = useState<OrigemOption[]>([]);
  const [loadingOrigem, setLoadingOrigem] = useState(false);
  const [empregadores, setEmpregadores] = useState<OrigemOption[]>([]);

  const consultaService = new ConsultaService();
  const empregadorService: EmpregadorService = new EmpregadorService();
  const pontoVendaService = new PontoVendaService();
  const promotoraService = new PromotoraService();

  const buscarConsultas = async (page = 1) => {

    if (!origem || !idOrigem) {
      setAlert({ message: 'Selecione a **Origem** e o **ID** correspondente.', type: 'warning' });
      return;
    }

    if (!dataInicial || !dataFinal) {
      setAlert({ message: 'Selecione o **Intervalo de Datas**.', type: 'warning' });
      return;
    }

    try {
      setLoading(true);
      setAlert(null);

      const response = await consultaService.listarPorPromotoraEmpregador(
        Number(idOrigem), 
        Number(idEmpregador),
        page - 1,
        10,
        dataInicial,
        dataFinal
      );

      const data: ConsultaPage = response.data;
      setConsultas(data.content || []);
      setTotalPages(data.totalPages || 1);
      setCurrentPage(page);
    } catch (err: any) {
      console.error(err);
      setAlert({ message: 'Erro ao buscar consultas.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => buscarConsultas(page);

  useEffect(() => {
    const carregarEmpregadores = async () => {
      try {
        const response = await empregadorService.listarTodosEmpregadoresAtivos();
        setEmpregadores(response.data || []);
      } catch (err) {
        console.error(err);
        setEmpregadores([]);
      }
    };
    carregarEmpregadores();
  }, []); 

  useEffect(() => {
    const carregarOpcoesOrigem = async () => {
      setOpcoesOrigem([]);
      setIdOrigem(''); 

      if (!origem) {
        return;
      }
      
      try {
        setLoadingOrigem(true);
        let response;
        
        if (origem === 'PONTO_VENDA') {
          // Usando listarTodosPontosVendas (POST) com paginação alta para buscar tudo
          response = await pontoVendaService.listarTodosPontosVendas(0, 9999, {});
          
          if (response && response.data && Array.isArray(response.data.content)) {
            setOpcoesOrigem(response.data.content as OrigemOption[]);
          }
          
        } else if (origem === 'PROMOTORA') {
          // Usando listarTodasPromotorasAtivas (GET)
          response = await promotoraService.listarTodasPromotorasAtivas();
          if (response && response.data) {
             setOpcoesOrigem(response.data);
          }
        }

      } catch (err) {
        console.error(`Erro ao carregar lista de ${origem}:`, err);
        setOpcoesOrigem([]);
        setAlert({ message: `Erro ao carregar a lista de ${origem === 'PONTO_VENDA' ? 'Pontos de Venda' : 'Promotoras'}.`, type: 'error' });
      } finally {
        setLoadingOrigem(false);
      }
    };

    carregarOpcoesOrigem();
  }, [origem]); 

  return (
    <>
      {alert && <CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
      <div className="position-relative m-md-3">
        <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between' }}>
          <h5>Consulta Informações Gerais &gt; Listagem de Consultas</h5>
          <Button id="App-button-cor-principal-escura" onClick={() => setFiltros(!filtros)}>
            <FontAwesomeIcon icon={faFilter} style={{ marginRight: '10px' }} /> Filtros
          </Button>
          <Button id="App-button-cor-principal-escura" onClick={() => window.history.back()}>
            Voltar
          </Button>
        </div>
        {filtros && (
          <>
            <Row className="mb-4" style={{ marginTop: '15px' }}>

              <Form.Group as={Col} md="2" controlId="formSelectOrigem">
                <Form.Label>Origem</Form.Label>
                <Form.Select 
                  value={origem} 
                  onChange={(e) => {
                    setOrigem(e.target.value as OrigemType);
                    setIdOrigem(''); 
                  }}
                >
                  <option value="">Selecione</option>
                  {OPCOES_ORIGEM.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              
              <Form.Group as={Col} md="3" controlId="formSelectIdOrigem">
                <Form.Label>ID da Origem</Form.Label>
                <Form.Select 
                  value={idOrigem} 
                  onChange={(e) => setIdOrigem(e.target.value)}
                  disabled={!origem || loadingOrigem} 
                >
                  <option value="">
                    {loadingOrigem ? 'Carregando...' : origem ? 'Selecione' : 'Selecione a Origem primeiro'}
                  </option>
                  {opcoesOrigem.map((op) => (
                    <option key={op.id} value={String(op.id)}>
                      {op.id} - {op.nome}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group as={Col} md="3" controlId="formSelectEmpregador">
                <Form.Label>Empregador</Form.Label>
                <Form.Select value={idEmpregador} onChange={(e) => setIdEmpregador(e.target.value)}>
                  <option value="0">Todos</option>
                  {empregadores.map((e) => (
                    <option key={e.id} value={String(e.id)}>
                      {e.id} - {e.nome}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group as={Col} md="2" controlId="formDataInicial">
                <Form.Label>Data Inicial</Form.Label>
                <Form.Control
                  type="date"
                  value={dataInicial}
                  onChange={(e) => setDataInicial(e.target.value)}
                />
              </Form.Group>
              <Form.Group as={Col} md="2" controlId="formDataFinal">
                <Form.Label>Data Final</Form.Label>
                <Form.Control
                  type="date"
                  value={dataFinal}
                  onChange={(e) => setDataFinal(e.target.value)}
                />
              </Form.Group>
            </Row>
            <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
              <Button id="App-button-cor-principal-escura" onClick={() => setFiltros(!filtros)}>
                <FontAwesomeIcon icon={faFilter} style={{ marginRight: '5px' }} /> Ocultar
              </Button>
              <Button id="App-button-cor-principal-escura" onClick={() => buscarConsultas(1)}>
                <FontAwesomeIcon icon={faRefresh} style={{ marginRight: '5px' }} /> Buscar
              </Button>
            </div>
          </>
        )}
        {loading ? (
          <Spinner loading={loading} />
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered table-striped">
              <thead>
                <tr>
                  <th>Origem</th>
                  <th>Empregador</th>
                  <th>Data</th>
                  <th>Qtde Consultas</th>
                </tr>
              </thead>
              <tbody>
                {consultas.length > 0 ? (
                  consultas.map((c) => (
                    <tr key={c.id}>
                      <td>{c.idOrigem + ' - ' + c.origem}</td>
                      <td>{c.idEmpregador + ' - ' + c.nomeEmpregador}</td>
                      <td>{c.data}</td>
                      <td>{c.nconsultas}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center">Para Realizar uma Consulta Use os Filtros.</td>
                  </tr>
                )}
              </tbody>
            </table>
            {consultas.length > 0 && (
              <ResponsivePagination
                current={currentPage}
                total={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        )}
      </div>
      <ScrollToTopButton />
    </>
  );
}

export default ListaConsultas;